import { decodeBase64Attachment } from '@qdabra/infopath-attachments';
import {
    initiateFileDownload,
    openFileInBrowser,
} from '../../../utility/download.helpers';
import {
    paramDsName,
    paramFileName,
    paramOpen,
    paramXPath,
} from '../constants/parameterNames';
import {
    typeBoolean,
    typeDataSource,
    typeXPath,
} from '../constants/parameterTypes';
import { failure, success } from '../helpers/commandResult';
import {
    getBoolParamValue,
    getParamValue,
} from '../helpers/parameter.accessors';
import { getDataSource } from '../helpers/qRules.dataSource.helpers';
import { getValidNode } from '../helpers/qRules.node.helpers';

const description =
    'Download a file that is stored as base64 in a form field, or open it in a new browser tab.';

const requiredParameters = {
    [paramXPath]: {
        description:
            'XPath to the field that contains the file.\n\nIf multiple fields are found, the one that comes first in the data source is used.',
        type: typeXPath,
        dsParentName: paramDsName,
    },
};

const optionalParameters = {
    [paramDsName]: {
        description:
            'The name of the data source that contains the field.\n\nIf not specified, the main data source is used.',
        type: typeDataSource,
    },
    [paramFileName]: {
        description:
            'The file name to use for the downloaded file.\n\nIf not specified, the name is determined as follows:\n(1) If the file name is stored in the base64, that filename will be used.\n(2) Otherwise, the command will attempt to auto-detect the file type and download it as Picture.XYZ, where XYZ is an auto-determined extension (gif, jpg, png, bmp).',
    },
    [paramOpen]: {
        description: `(boolean, default: false) If true, will attempt to open the file in a new browser tab instead of downloading.\n\nIf the file type is not supported by the browser, the file will be downloaded instead.\n\nWhen using this option, /${paramFileName} does not have an effect on the name of the opened file, but extension of the the specified name is used in determining how to handle the file.`,
        type: typeBoolean,
    },
};

const doDownload = (fileBytes, fileName, open) => {
    if (open) {
        openFileInBrowser(fileBytes, fileName);
    } else {
        const downloadResult = initiateFileDownload(fileBytes, fileName);

        if (!downloadResult) {
            return failure('Failed to initiate file download.');
        }
    }

    return success();
};

const executeAsync = async ({ dataSources, parameters }) => {
    const xpath = getParamValue(parameters, paramXPath);
    const dsName = getParamValue(parameters, paramDsName);
    const fileName = getParamValue(parameters, paramFileName);
    const open = getBoolParamValue(parameters, paramOpen, false);

    const dataSource = getDataSource(dataSources, dsName);
    const node = getValidNode(
        dataSource,
        xpath,
        'Could not locate attachment field.',
    );

    const nodeValue = node.value();

    if (!nodeValue) {
        return failure('Attachment field is empty.');
    }

    const attachment = decodeBase64Attachment(nodeValue);

    const fileNameToUse = fileName || attachment.name;

    return doDownload(attachment.bytes, fileNameToUse, open);
};

const DownloadAttachment = {
    description,
    executeAsync,
    requiredParameters,
    optionalParameters,
};

export default DownloadAttachment;
